import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { z } from "zod";

import {
  fetchRegistrationStatus,
  getApiErrorCode,
  getApiErrorMessage,
  registerWithoutOtpRequest,
  type AuthUser,
  type WorkForCandidate,
} from "@penn/web-common";
import {
  PennButton,
  PennDropdown,
  PennField,
  PennInput,
  PennModal,
  PennText,
} from "@/components/ui";
import { useRegions } from "@/hooks/useRegions";
import { useAssignRole, useRoles } from "@/hooks/useUsers";
import { useAppSelector } from "@/stores/hooks";

const REGION_CODES = ["EU", "NA", "ASIA"] as const;

const stageSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email("Enter a valid email address")
    .max(100, "100 characters or fewer"),
  regionCode: z.enum(REGION_CODES).optional(),
});
type StageFormData = z.infer<typeof stageSchema>;

const ERROR_MESSAGES: Record<string, string> = {
  "duplicate-email": "An account with this email already exists.",
  "region-required": "Select a region. It is required for a customer.",
  "domain-check-unavailable":
    "Could not check this email's domain right now, so nothing was created. Try again shortly.",
  "cognito-admin-unavailable":
    "Staging users is not available in this environment (Cognito administration is not configured).",
  "permission-denied": "You do not have permission to stage users.",
};

function stageErrorMessage(err: unknown): string {
  const code = getApiErrorCode(err);
  return (
    (code && ERROR_MESSAGES[code]) ||
    getApiErrorMessage(err, "Could not stage this user.")
  );
}

/** What an email that already has an account means for the caller. */
export type ExistingAccount =
  | { kind: "self" }
  | { kind: "candidate"; candidate: WorkForCandidate }
  /** Internal, or outside the caller's region: not a Work-For target. */
  | { kind: "blocked" };

/** Pause after the last keystroke before the email is looked up. */
const CHECK_DEBOUNCE_MS = 400;
const emailShape = stageSchema.shape.email;

type StageUserModalProps = {
  open: boolean;
  onClose: () => void;
  /** Resolve an email that already has an account. */
  resolveRegistered: (email: string) => Promise<ExistingAccount>;
  /** Switch Work-For to that account; false if it could not be applied. */
  onSelectExisting: (existing: ExistingAccount) => Promise<boolean>;
  /** A customer was staged; the selector makes them the Work-For target. */
  onStagedCustomer: (user: AuthUser) => Promise<void>;
};

/**
 * Work-For → Other: stage a person who has no account, by email only. Names
 * are never entered here; they come from the identity provider when the
 * person signs up. The server decides internal vs external from the email. A
 * customer becomes the Work-For target; an internal user cannot be one, so the
 * dialog offers a role instead.
 */
export function StageUserModal({
  open,
  onClose,
  resolveRegistered,
  onSelectExisting,
  onStagedCustomer,
}: StageUserModalProps) {
  const navigate = useNavigate();
  const canManageUsers = useAppSelector(
    (s) => s.auth.user?.role?.canManageUsers === true,
  );
  const regions = useRegions();
  const roles = useRoles();
  const assignRole = useAssignRole();
  const [stagedInternal, setStagedInternal] = useState<AuthUser | null>(null);
  const [roleCode, setRoleCode] = useState("");
  const [lookupError, setLookupError] = useState<string | null>(null);
  /** Result of the as-you-type check, for exactly `existing.email`. */
  const [existing, setExisting] = useState<{
    email: string;
    account: ExistingAccount | null;
  } | null>(null);
  const [checking, setChecking] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestCheckRef = useRef("");

  const regionOptions = useMemo(
    () =>
      (regions.data ?? []).map((r) => ({
        value: r.code,
        label: r.displayName,
      })),
    [regions.data],
  );
  const internalRoleOptions = useMemo(
    () =>
      (roles.data ?? [])
        .filter((r) => r.isActive && r.isInternal)
        .map((r) => ({ value: r.code, label: r.displayName })),
    [roles.data],
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<StageFormData>({
    resolver: zodResolver(stageSchema),
    defaultValues: { email: "" },
  });

  const stage = useMutation({ mutationFn: registerWithoutOtpRequest });
  const watchedEmail = useWatch({ control, name: "email" }) ?? "";
  const normalizedEmail = watchedEmail.trim().toLowerCase();

  /** Look an email up. Null means no account; throws if the lookup failed. */
  const lookUp = useCallback(
    async (email: string): Promise<ExistingAccount | null> => {
      const { registered } = await fetchRegistrationStatus(email);
      return registered ? resolveRegistered(email) : null;
    },
    [resolveRegistered],
  );

  const checkNow = useCallback(
    async (email: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = null;
      if (!emailShape.safeParse(email).success) return;
      if (latestCheckRef.current === email) return; // already checked or in flight
      latestCheckRef.current = email;
      setChecking(true);
      try {
        const account = await lookUp(email);
        // A newer email has been typed since; drop this answer.
        if (latestCheckRef.current === email) setExisting({ email, account });
      } catch {
        // Not fatal: the submit path checks again and reports the failure.
        if (latestCheckRef.current === email) latestCheckRef.current = "";
      } finally {
        if (latestCheckRef.current === email || latestCheckRef.current === "") {
          setChecking(false);
        }
      }
    },
    [lookUp],
  );

  // Debounced: each keystroke restarts one timer; the lookup runs only after a pause.
  useEffect(() => {
    if (!open) return;
    if (existing && existing.email !== normalizedEmail) setExisting(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void checkNow(normalizedEmail);
    }, CHECK_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // `existing` is read, not reacted to: only a change of email reschedules.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, normalizedEmail, checkNow]);

  // Every opening starts clean, with the cursor in the email field.
  useEffect(() => {
    if (!open) return;
    reset({ email: "" });
    stage.reset();
    assignRole.reset();
    setStagedInternal(null);
    setRoleCode("");
    setLookupError(null);
    setExisting(null);
    setChecking(false);
    latestCheckRef.current = "";
    const id = setTimeout(() => setFocus("email"), 0);
    return () => clearTimeout(id);
    // reset/setFocus and the mutation handles are stable; only `open` matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = async (data: StageFormData) => {
    setLookupError(null);
    stage.reset();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    let account: ExistingAccount | null;
    if (existing?.email === data.email) {
      account = existing.account;
    } else {
      try {
        account = await lookUp(data.email);
      } catch (e) {
        setLookupError(
          getApiErrorMessage(e, "Could not check that email. Try again."),
        );
        return;
      }
      latestCheckRef.current = data.email;
      setExisting({ email: data.email, account });
    }
    if (account) {
      if (account.kind === "blocked") return; // explained under the field
      if (await onSelectExisting(account)) onClose();
      else setLookupError("Could not switch to that account. Try again.");
      return;
    }
    try {
      const user = await stage.mutateAsync({
        email: data.email,
        ...(data.regionCode ? { regionCode: data.regionCode } : {}),
      });
      if (user.isInternal) {
        setStagedInternal(user);
        return;
      }
      await onStagedCustomer(user);
      onClose();
    } catch {
      /* shown from stage.error */
    }
  };

  const finishInternal = () => {
    onClose();
    navigate("/admin/users");
  };

  if (!open) return null;

  const busy = isSubmitting || stage.isPending;
  const current =
    existing && existing.email === normalizedEmail ? existing.account : null;
  const accountNote: { tone: "info" | "blocked"; text: string } | null =
    checking && !current
      ? { tone: "info", text: "Checking…" }
      : current?.kind === "self"
        ? { tone: "info", text: "That is your own account." }
        : current?.kind === "candidate"
          ? {
              tone: "info",
              text: "Already has an account. You'll work for them instead of creating a new one.",
            }
          : current?.kind === "blocked"
            ? {
                tone: "blocked",
                text: "This email already has an account you can't work for (internal, or outside your region). Manage it on the Users page.",
              }
            : null;
  const submitLabel = busy
    ? "Working…"
    : current?.kind === "candidate"
      ? "Work for them"
      : current?.kind === "self"
        ? "Work as myself"
        : "Stage user";
  const error =
    lookupError ?? (stage.isError ? stageErrorMessage(stage.error) : null);

  return createPortal(
    <PennModal
      open
      onClose={onClose}
      title={stagedInternal ? "Internal user staged" : "Stage a new user"}
      subtitle={stagedInternal?.email}
      maxWidth="max-w-lg"
    >
      <div className="px-6 py-5">
        {stagedInternal ? (
          <div className="space-y-5">
            <PennText as="p" size="sm" color="blue_grey1">
              {stagedInternal.email} is an internal address, so it cannot be a
              Work-For target. This user needs an explicit role assigned for
              access.
            </PennText>

            {canManageUsers ? (
              <>
                <PennField label="Role" htmlFor="stagedRole">
                  <PennDropdown
                    inputId="stagedRole"
                    options={internalRoleOptions}
                    value={roleCode}
                    onChange={(v) =>
                      setRoleCode(typeof v === "string" ? v : "")
                    }
                    placeholder={
                      roles.isLoading ? "Loading roles…" : "Select a role"
                    }
                    isDisabled={roles.isLoading}
                  />
                </PennField>
                {assignRole.isError && (
                  <div
                    role="alert"
                    className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
                  >
                    {getApiErrorMessage(
                      assignRole.error,
                      "Could not assign the role.",
                    )}
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <PennButton
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={finishInternal}
                  >
                    Skip
                  </PennButton>
                  <PennButton
                    type="button"
                    variant="primary"
                    size="md"
                    fullWidth
                    className="bg-penn-500"
                    disabled={!roleCode || assignRole.isPending}
                    onClick={() =>
                      assignRole.mutate(
                        { id: stagedInternal.id, roleCode },
                        { onSuccess: finishInternal },
                      )
                    }
                  >
                    {assignRole.isPending ? "Assigning…" : "Assign role"}
                  </PennButton>
                </div>
              </>
            ) : (
              <>
                <PennText as="p" size="sm" color="blue_grey1">
                  Ask a user administrator to assign their role.
                </PennText>
                <PennButton
                  type="button"
                  variant="primary"
                  size="md"
                  className="bg-penn-500"
                  onClick={onClose}
                >
                  Done
                </PennButton>
              </>
            )}
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="space-y-5"
          >
            <PennText as="p" size="sm" color="blue_grey1">
              Creates the account without sending a code. Whether it is internal
              or a customer is decided from the email address; the name comes
              from their sign-up.
            </PennText>

            <PennField
              label="Email"
              htmlFor="stageEmail"
              required
              error={errors.email?.message}
            >
              <PennInput
                id="stageEmail"
                type="email"
                autoComplete="off"
                placeholder="name@company.com"
                error={!!errors.email}
                {...register("email", {
                  onBlur: (e) =>
                    void checkNow(String(e.target.value).trim().toLowerCase()),
                })}
              />
              {/* Always rendered with room for two lines, so the dialog (centred on
                  screen) never changes height as the check comes and goes. */}
              <p
                role="status"
                aria-live="polite"
                data-testid="stage-email-status"
                className={`mt-1 min-h-8 text-xs leading-4 ${
                  accountNote?.tone === "blocked"
                    ? "text-red-600"
                    : "text-slate-600"
                }`}
              >
                {accountNote?.text ?? ""}
              </p>
            </PennField>

            <PennField
              label="Region"
              htmlFor="regionCode"
              error={errors.regionCode?.message}
            >
              <Controller
                name="regionCode"
                control={control}
                render={({ field }) => (
                  <PennDropdown
                    inputId="regionCode"
                    name={field.name}
                    options={regionOptions}
                    value={field.value ?? ""}
                    onChange={(v) => field.onChange(v || undefined)}
                    onBlur={field.onBlur}
                    placeholder={
                      regions.isLoading ? "Loading regions…" : "Select a region"
                    }
                    isDisabled={regions.isLoading}
                  />
                )}
              />
              <PennText as="p" size="xs" color="blue_grey1" className="mt-1">
                Required for a customer. For an internal user it sets their home
                region; leave it blank to use yours.
              </PennText>
            </PennField>

            {error && (
              <div
                role="alert"
                className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
              >
                {error}
              </div>
            )}

            <div className="flex items-center gap-3 pt-2">
              <PennButton
                type="button"
                variant="secondary"
                size="md"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </PennButton>
              <PennButton
                type="submit"
                variant="primary"
                size="md"
                fullWidth
                disabled={busy || current?.kind === "blocked"}
                className="bg-penn-500"
              >
                {submitLabel}
              </PennButton>
            </div>
          </form>
        )}
      </div>
    </PennModal>,
    document.body,
  );
}
